/**
 * Links Enhancement
 *
 * Automatically enhance links with additional information:
 * - Add "(PDF)" to links pointing to PDF files when not already mentioned
 * - Add external link icon to links pointing to external domains
 * - Add screen reader friendly text for better accessibility
 * - Ensure external links open in new windows with proper security
 */

(function () {
  "use strict";

  /**
   * Check if a URL points to a PDF file
   * @param {string} url - The URL to check
   * @returns {boolean} - True if URL points to a PDF
   */
  function isPdfUrl(url) {
    if (!url) return false;

    // Remove query parameters and fragments for checking
    const cleanUrl = url.split("?")[0].split("#")[0];
    return cleanUrl.toLowerCase().endsWith(".pdf");
  }

  /**
   * Check if text already mentions PDF
   * @param {string} text - The text to check
   * @returns {boolean} - True if text contains PDF reference
   */
  function hasPdfMention(text) {
    if (!text) return false;

    const lowerText = text.toLowerCase();
    return (
      lowerText.includes("pdf") ||
      lowerText.includes("portable document format")
    );
  }

  /**
   * Check if a URL is external (different domain)
   * @param {string} url - The URL to check
   * @returns {boolean} - True if URL is external
   */
  function isExternalUrl(url) {
    if (!url) return false;

    // Skip non-http(s) links (mailto, tel, javascript, etc.)
    if (!url.match(/^https?:\/\//i)) return false;

    try {
      const linkHost = new URL(url).hostname;
      const currentHost = window.location.hostname;
      return linkHost !== currentHost;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get localized PDF text
   * @returns {string} - Localized PDF indicator text
   */
  function getPdfText() {
    // Check if WordPress localization is available
    if (
      window.heltti_localization &&
      window.heltti_localization.pdf_indicator
    ) {
      return window.heltti_localization.pdf_indicator;
    }

    // Fallback to default text
    return "(PDF)";
  }

  /**
   * Get localized screen reader text for PDF links
   * @returns {string} - Localized screen reader text
   */
  function getPdfScreenReaderText() {
    if (window.heltti_localization && window.heltti_localization.opens_pdf) {
      return " (" + window.heltti_localization.opens_pdf + ")";
    }

    return " (opens PDF document)";
  }

  /**
   * Get localized description text for PDF links
   * @returns {string} - Localized description text
   */
  function getPdfDescriptionText() {
    if (
      window.heltti_localization &&
      window.heltti_localization.pdf_description
    ) {
      return window.heltti_localization.pdf_description;
    }

    return "This link opens a PDF document";
  }

  /**
   * Get localized external link text
   * @returns {string} - Localized external link indicator text
   */
  function getExternalText() {
    if (
      window.heltti_localization &&
      window.heltti_localization.external_indicator
    ) {
      return window.heltti_localization.external_indicator;
    }

    return "(external link)";
  }

  /**
   * Get localized screen reader text for external links
   * @returns {string} - Localized screen reader text
   */
  function getExternalScreenReaderText() {
    if (
      window.heltti_localization &&
      window.heltti_localization.opens_external
    ) {
      return " (" + window.heltti_localization.opens_external + ")";
    }

    return " (opens in new window)";
  }

  /**
   * Get localized description text for external links
   * @returns {string} - Localized description text
   */
  function getExternalDescriptionText() {
    if (
      window.heltti_localization &&
      window.heltti_localization.external_description
    ) {
      return window.heltti_localization.external_description;
    }

    return "This link opens in a new window";
  }

  /**
   * Create SVG icon for external links
   * @returns {string} - SVG markup for external icon
   */
  function createExternalIcon() {
    // sprite_url is localized from PHP via get_template_directory_uri() so it works in any environment
    const spriteUrl =
      (window.heltti_localization && window.heltti_localization.sprite_url) ||
      "/wp-content/themes/mindy2026/dist/sprite/sprite.svg";

    return (
      '<svg class="icon icon-external" aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 28 28" style="margin-inline-start:0.25em; width: 0.9em;height: 0.9em">' +
      '<use href="' + spriteUrl + '#icon-external"></use>' +
      "</svg>"
    );
  }

  /**
   * Add PDF indicator to a link
   * @param {HTMLAnchorElement} link - The link element to enhance
   */
  function addPdfIndicator(link) {
    const pdfText = getPdfText();

    // Add visual indicator
    const currentText = link.textContent.trim();
    if (currentText) {
      link.innerHTML =
        link.innerHTML +
        ' <span class="link-type-indicator">' +
        pdfText +
        "</span>";
    }

    // Add screen reader text for accessibility
    const srText = document.createElement("span");
    srText.className = "screen-reader-text";
    srText.textContent = getPdfScreenReaderText();
    link.appendChild(srText);

    // Add appropriate ARIA attributes
    link.setAttribute(
      "aria-describedby",
      link.getAttribute("aria-describedby")
        ? link.getAttribute("aria-describedby") + " pdf-link-description"
        : "pdf-link-description"
    );

    // Add data attribute for styling
    link.setAttribute("data-link-type", "pdf");
  }

  /**
   * Add external link indicator to a link
   * @param {HTMLAnchorElement} link - The link element to enhance
   */
  function addExternalIndicator(link) {
    // Add visual indicator with SVG icon
    const indicator = document.createElement("span");
    indicator.className = "link-type-indicator link-external-indicator";
    indicator.innerHTML =
      createExternalIcon() +
      ' <span class="link-external-text screen-reader-text">' +
      getExternalText() +
      "</span>";
    link.appendChild(indicator);

    // Add screen reader text for accessibility
    const srText = document.createElement("span");
    srText.className = "screen-reader-text";
    srText.textContent = getExternalScreenReaderText();
    link.appendChild(srText);

    // Add appropriate ARIA attributes
    link.setAttribute(
      "aria-describedby",
      link.getAttribute("aria-describedby")
        ? link.getAttribute("aria-describedby") + " external-link-description"
        : "external-link-description"
    );

    // Ensure external links open in new tab with proper security
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");

    // Add data attribute for styling
    link.setAttribute("data-link-type", "external");
  }

  /**
   * Process all links on the page
   */
  function processLinks() {
    const links = document.querySelectorAll("a[href]");

    links.forEach((link) => {
      const href = link.getAttribute("href");
      const linkText = link.textContent || link.innerText || "";

      // Skip if already processed
      if (link.querySelector(".link-type-indicator")) {
        return;
      }

      // Skip if no text content
      if (!linkText.trim()) {
        return;
      }

      // Check for PDF links
      if (isPdfUrl(href) && !hasPdfMention(linkText)) {
        addPdfIndicator(link);
        return; // Don't process as external if it's a PDF
      }

      // Check for external links
      if (isExternalUrl(href)) {
        addExternalIndicator(link);
      }
    });
  }

  /**
   * Create hidden description element for screen readers
   */
  function createAccessibilityDescription() {
    // PDF description
    if (!document.getElementById("pdf-link-description")) {
      const pdfDescription = document.createElement("div");
      pdfDescription.id = "pdf-link-description";
      pdfDescription.className = "screen-reader-text";
      pdfDescription.textContent = getPdfDescriptionText();
      document.body.appendChild(pdfDescription);
    }

    // External link description
    if (!document.getElementById("external-link-description")) {
      const externalDescription = document.createElement("div");
      externalDescription.id = "external-link-description";
      externalDescription.className = "screen-reader-text";
      externalDescription.textContent = getExternalDescriptionText();
      document.body.appendChild(externalDescription);
    }
  }

  /**
   * Initialize the links enhancement
   */
  function init() {
    // Create accessibility description
    createAccessibilityDescription();

    // Process existing links
    processLinks();

    // Set up mutation observer for dynamically added content
    if (window.MutationObserver) {
      const observer = new MutationObserver(function (mutations) {
        let shouldProcess = false;

        mutations.forEach(function (mutation) {
          if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
            // Check if any added nodes contain links
            for (let i = 0; i < mutation.addedNodes.length; i++) {
              const node = mutation.addedNodes[i];
              if (node.nodeType === Node.ELEMENT_NODE) {
                if (node.tagName === "A" || node.querySelector("a[href]")) {
                  shouldProcess = true;
                  break;
                }
              }
            }
          }
        });

        if (shouldProcess) {
          // Debounce processing to avoid excessive calls
          clearTimeout(init.timeout);
          init.timeout = setTimeout(processLinks, 100);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Expose function globally for manual processing if needed
  window.heltti = window.heltti || {};
  window.heltti.processLinks = processLinks;
})();
